import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useGraphiQL } from "@graphiql/react";
import { lexicographicSortSchema, printSchema } from "grafast/graphql";
import { useCallback, useMemo, useState } from "react";
import { transformSchema } from "../utils/transformSchema.js";
function useToggle(initialState = false) {
    const [checked, setChecked] = useState(initialState);
    const onChange = useCallback((e) => {
        setChecked(e.target.checked);
    }, []);
    const reset = useCallback(() => void setChecked(initialState), [initialState]);
    return useMemo(() => ({
        checked,
        setChecked,
        reset,
        changed: checked !== initialState,
        attrs: { checked, onChange },
    }), [checked, initialState, onChange, reset]);
}
function download(contents, filename, type = "application/graphql") {
    const blob = new Blob([contents], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}
export const Download = () => {
    const [error, setError] = useState(null);
    const sort = useToggle(false);
    const descs = useToggle(true);
    const depr = useToggle(true);
    const changed = sort.changed || descs.changed || depr.changed;
    const reset = useCallback(() => {
        sort.reset();
        descs.reset();
        depr.reset();
    }, [depr, descs, sort]);
    const schema = useGraphiQL((sel) => sel.schema);
    const sdl = useMemo(() => {
        if (!schema)
            return null;
        let toExport = schema;
        if (!descs.checked || !depr.checked) {
            toExport = transformSchema(toExport, {
                trimDescriptions: !descs.checked,
                trimDeprecated: !depr.checked,
            });
        }
        if (sort.checked) {
            toExport = lexicographicSortSchema(toExport);
        }
        const string = printSchema(toExport) + "\n";
        return string;
    }, [schema, descs.checked, depr.checked, sort.checked]);
    const submit = useCallback((e) => {
        e.preventDefault();
        setError(null);
        if (!sdl) {
            setError(new Error("Schema is not available to be exported"));
            return;
        }
        const filenameParts = ["schema"];
        if (!depr.checked) {
            filenameParts.push("no-deprecated");
        }
        if (!descs.checked) {
            filenameParts.push("no-descriptions");
        }
        if (sort.checked) {
            filenameParts.push("sorted");
        }
        download(sdl, `${filenameParts.join(".")}.graphql`);
    }, [depr.checked, descs.checked, sdl, sort.checked]);
    const copy = useCallback((e) => {
        e.preventDefault();
        setError(null);
        if (!sdl) {
            setError(new Error("Schema is not available to be copied"));
            return;
        }
        navigator.clipboard.writeText(sdl).catch((e) => {
            setError(new Error("Error copying text to clipboard", { cause: e }));
        });
    }, [sdl]);
    return (_jsxs("div", { children: [_jsxs("form", { onSubmit: submit, children: [_jsxs("p", { children: [_jsxs("label", { children: [_jsx("input", { type: "checkbox", ...sort.attrs }), " Sorted (lexicographically)"] }), _jsx("br", {}), _jsxs("label", { children: [_jsx("input", { type: "checkbox", ...descs.attrs }), " Include descriptions"] }), _jsx("br", {}), _jsxs("label", { children: [_jsx("input", { type: "checkbox", ...depr.attrs }), " Include deprecated"] })] }), error ? _jsx("p", { className: "error", children: error.message }) : null, _jsxs("div", { children: [_jsx("button", { type: "submit", children: "Download" }), _jsx("button", { type: "button", onClick: copy, children: "Copy" }), _jsx("button", { type: "button", disabled: !changed, onClick: reset, children: "Reset" })] })] }), _jsx("pre", { style: { fontSize: "0.75rem" }, children: _jsx("code", { children: sdl }) }), " "] }));
};
//# sourceMappingURL=Download.js.map